import React, { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import axios from '../api';
import { useLocation, useNavigate } from 'react-router-dom';
import UpdateMultipleTrialsComponent from '../components/UpdateMultipleTrialsComponent';
import MassCompleteTasks from '../components/MassCompleteTasks';
import { updateUserSettings } from '../settings';
import TrialsTable from '../components/TrialsTable';

// Get current user ID from localStorage (adjust if you use context or props)
const getCurrentUserId = () => {
  try {
    const userRaw = sessionStorage.getItem('userInfo');
    if (!userRaw) return null;
    let user;
    try {
      user = JSON.parse(userRaw);
    } catch {
      user = userRaw;
    }
    const userData = user?.data ?? user;
    return userData?.id ?? userData?.userId ?? userData?.userid ?? null;
  } catch (e) {
    return null;
  }
};

export default function TrialsOverview() {
  const navigate = useNavigate();
  // The list's URL (its filters are in the query string), so a trial's Back returns to the same list
  const location = useLocation();
  const fromList = { state: { from: location.pathname + location.search } };

  // Mass update mode toggle
  const [massUpdateMode, setMassUpdateMode] = useState(false);
  // What mass update does to the selected trials: update fields, or complete a task
  const [massMode, setMassMode] = useState('fields');
  const [users, setUsers] = useState([]);
  const [usersById, setUsersById] = useState({});
  // State for selected trial IDs in mass update mode
  const [selectedTrialIds, setSelectedTrialIds] = useState([]);

  // Clear selection when mass update mode is turned off
  useEffect(() => {
    if (!massUpdateMode) {
      setSelectedTrialIds([]);
    }
  }, [massUpdateMode]);
  const [perPage, setPerPage] = useState(() => {
    const stored = sessionStorage.getItem('trialsPerPage');
    if (stored === 'all') return 'all';
    const num = stored ? Number(stored) : 15;
    return [10, 15, 25, 50, 100].includes(num) ? num : 15;
  });
  // Store perPage in sessionStorage whenever it changes
  useEffect(() => {
    sessionStorage.setItem('trialsPerPage', String(perPage));
    const t = setTimeout(() => {
      updateUserSettings({ trials_per_page: perPage });
    }, 300);
    return () => clearTimeout(t);
  }, [perPage]);

  // Hydrate perPage from settings events
  useEffect(() => {
    const applyPerPage = () => {
      try {
        const stored = sessionStorage.getItem('trialsPerPage');
        if (!stored) return;
        if (stored === 'all') {
          if (perPage !== 'all') setPerPage('all');
        } else {
          const num = Number(stored);
          if ([10, 15, 25, 50, 100].includes(num) && perPage !== num) setPerPage(num);
        }
      } catch { }
    };
    window.addEventListener('trialsPerPageHydrated', applyPerPage);
    window.addEventListener('userSettingsUpdated', applyPerPage);
    applyPerPage();
    return () => {
      window.removeEventListener('trialsPerPageHydrated', applyPerPage);
      window.removeEventListener('userSettingsUpdated', applyPerPage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [customers, setCustomers] = useState([]);
  const [currentUserId, setCurrentUserId] = useState(getCurrentUserId());
  const [searchQuery, setSearchQuery] = useState('');
  const [editValues, setEditValues] = useState({});
  const [showCopyToDialog, setShowCopyToDialog] = useState(false);
  const [newTrialNumber, setNewTrialNumber] = useState('');

  // Listen for sessionStorage changes and page focus
  useEffect(() => {
    const handleStorage = (e) => {
      if (e.key === 'user') {
        setCurrentUserId(getCurrentUserId());
      }
    };
    const handleFocus = () => {
      setCurrentUserId(getCurrentUserId());
    };
    window.addEventListener('storage', handleStorage);
    window.addEventListener('focus', handleFocus);
    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  // Fetch customers and users on mount
  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const res = await axios.post('/masterdata/customers', {});
        setCustomers(res.data || []);
      } catch {
        setCustomers([]);
      }
    };
    const fetchUsers = async () => {
      try {
        const res = await axios.post('/masterdata/users', {});
        const raw = res.data || [];
        // reduce to minimal shape: id, name, email
        const mapped = (Array.isArray(raw) ? raw : []).map(u => {
          const id = u?.id ?? u?.userId ?? u?.userid ?? '';
          const name = u?.name ?? u?.Name ?? u?.displayName ?? u?.display_name ?? u?.fullname ?? `${u?.first_name || ''} ${u?.last_name || ''}`.trim() ?? '';
          const email = u?.email ?? u?.Email ?? '';
          return { id, name, email };
        });
        setUsers(mapped);
        const byId = {};
        mapped.forEach(u => { if (u && u.id != null && u.id !== '') byId[String(u.id)] = u; });
        setUsersById(byId);
      } catch {
        setUsers([]);
        setUsersById({});
      }
    };
    fetchCustomers();
    fetchUsers();
  }, []);

  // Debug info panel
  const [debugUserRaw, setDebugUserRaw] = useState(() => sessionStorage.getItem('user'));
  const [debugParsedUser, setDebugParsedUser] = useState(() => {
    try {
      return JSON.parse(sessionStorage.getItem('user'));
    } catch {
      return sessionStorage.getItem('user');
    }
  });
  useEffect(() => {
    setDebugUserRaw(sessionStorage.getItem('user'));
    try {
      setDebugParsedUser(JSON.parse(sessionStorage.getItem('user')));
    } catch {
      setDebugParsedUser(sessionStorage.getItem('user'));
    }
  }, [currentUserId]);

  const handleReloadUser = () => {
    setCurrentUserId(getCurrentUserId());
    setDebugUserRaw(sessionStorage.getItem('user'));
    try {
      setDebugParsedUser(JSON.parse(sessionStorage.getItem('user')));
    } catch {
      setDebugParsedUser(sessionStorage.getItem('user'));
    }
  };

  let userType = typeof debugParsedUser;
  let userKeys = debugParsedUser && typeof debugParsedUser === 'object' ? Object.keys(debugParsedUser).join(', ') : '';
  let idVal = debugParsedUser && typeof debugParsedUser === 'object' ? debugParsedUser.id : '';
  let userIdVal = debugParsedUser && typeof debugParsedUser === 'object' ? debugParsedUser.userId : '';
  let useridVal = debugParsedUser && typeof debugParsedUser === 'object' ? debugParsedUser.userid : '';

  const handleApplyToFiltered = () => {
    // Build payload, omitting SD/FT when user selected "Leave ... unchanged" (empty string)
    const payload = {};
    // copy over simple text fields from editValues
    for (const [k, v] of Object.entries(editValues || {})) {
      if (v === '' || v == null) continue; // skip empty values (means no change)
      // map SD/FT to backend field names
      if (k === 'SD') {
        // studydirector should be sent as a single id or null; we're only sending if changed
        payload.studydirector = v;
        continue;
      }
      if (k === 'FT') {
        payload.field_technician = v;
        continue;
      }
      if (k === 'PI') {
        // principal_investigator should be sent as id
        payload.principal_investigator = v;
        continue;
      }
      // other key mappings can be adjusted here if needed
      payload[k] = v;
    }

    console.log('Applying update payload to filtered trials:', payload);

  };

  const confirmCopy = () => {
    console.log('Confirm copy to new trial:', newTrialNumber);
    setShowCopyToDialog(false);
    setNewTrialNumber('');
    // Add your logic to copy trial
  };

  // NEW: Add refresh mechanism for TrialsTable
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  // A ref, not state: passing a function to a state setter would make React call it as an
  // updater (i.e. run a fetch while rendering)
  const trialsTableRefreshRef = useRef(null);
  const handleTableRefresh = useCallback((fn) => { trialsTableRefreshRef.current = fn; }, []);

  // Enhanced fetchTrials that can be called from mass update
  const refreshTrialsData = async () => {
    const trialsTableRefresh = trialsTableRefreshRef.current;
    // Call the TrialsTable's refresh function if available
    if (trialsTableRefresh && typeof trialsTableRefresh === 'function') {
      try {
        await trialsTableRefresh();
      } catch (error) {
        console.error('Error refreshing via trialsTableRefresh:', error);
        // Fallback to trigger refresh
        setRefreshTrigger(prev => prev + 1);
      }
    } else {
      // Fallback: trigger refresh via prop
      setRefreshTrigger(prev => prev + 1);
    }
  };

  return (
    <div className="w-full p-4 flex flex-col h-full">
      {currentUserId == null && (
        <div className="mb-4 p-2 rounded bg-orange-100 text-orange-800 text-sm font-semibold">
          Warning: No user ID found in sessionStorage. Edit buttons will not appear. Please log in again or check your authentication setup.
        </div>
      )}

      <div className="mb-6 flex flex-col sm:flex-row sm:items-center gap-4 flex-shrink-0">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex-1">Trials Overview</h1>
        <button
          type="button"
          className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-semibold focus:outline-none transition-colors
            ${massUpdateMode ? 'bg-blue-600 text-white border-blue-700 hover:bg-blue-700' : 'bg-white text-gray-800 border-gray-300 hover:bg-gray-100 dark:bg-gray-800 dark:text-gray-200 dark:border-gray-700 dark:hover:bg-gray-700'}`}
          onClick={() => setMassUpdateMode(m => !m)}
        >
          {massUpdateMode ? 'Disable Mass Update Mode' : 'Enable Mass Update Mode'}
        </button>
      </div>

      {/* Conditionally render Update Multiple Trials card */}
      {massUpdateMode && (
        <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
          <div className="mb-4 flex flex-wrap items-center gap-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Mass Update Trials</h2>
            <div role="tablist" aria-label="Mass update" className="inline-flex rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden text-sm font-semibold">
              {[['fields', 'Update fields'], ['complete', 'Complete task']].map(([mode, label]) => (
                <button
                  key={mode}
                  type="button"
                  role="tab"
                  aria-selected={massMode === mode}
                  onClick={() => setMassMode(mode)}
                  className={`px-3 py-1.5 ${massMode === mode ? 'bg-emerald-700 text-white' : 'bg-white text-gray-800 hover:bg-gray-100 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700'}`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          {massMode === 'complete' ? (
            <MassCompleteTasks
              selectedTrialIds={selectedTrialIds}
              setSelectedTrialIds={setSelectedTrialIds}
              refreshTrials={refreshTrialsData}
            />
          ) : (
          <UpdateMultipleTrialsComponent
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            editValues={editValues}
            setEditValues={setEditValues}
            handleApplyToFiltered={handleApplyToFiltered}
            users={users}
            showCopyToDialog={showCopyToDialog}
            setShowCopyToDialog={setShowCopyToDialog}
            newTrialNumber={newTrialNumber}
            setNewTrialNumber={setNewTrialNumber}
            confirmCopy={confirmCopy}
            selectedTrialIds={selectedTrialIds}
            setSelectedTrialIds={setSelectedTrialIds}
            refreshTrials={refreshTrialsData} // Pass the enhanced refresh function
          />
          )}
        </div>
      )}

      <TrialsTable
        showSearchFilters={true}
        syncFiltersToUrl={true}
        rememberFiltersAs="trial_filters"
        showCustomerFilter={true}
        showProtocolFilter={true}
        showStudyDirectorFilter={true}
        showColumnConfig={true}
        columnOrderStorageKey="trialsOverviewColumns"
        perPageStorageKey="trialsPerPage"
        onRowClick={(trial) => navigate(`/trials/${trial.id}`, fromList)}
        onTrialSelect={(trial) => navigate(`/trials/${trial.id}`, fromList)}
        className="flex-1 min-h-0"
        massUpdateMode={massUpdateMode}
        selectedTrialIds={selectedTrialIds}
        onSelectionChange={setSelectedTrialIds}
        onCopyTrial={(trial) => navigate(`/trials/new`, { state: { trialId: trial.id } })}
        onEditTrial={(trial) => navigate(`/trials/${trial.id}/edit`, fromList)}
        refreshTrigger={refreshTrigger} // NEW: Pass refresh trigger
        onRefresh={handleTableRefresh}
      />
    </div>
  );
}